import { useEffect, useState } from 'react';
import './App.css';
import Header from "./components/header"
import InputDetails from "./components/inputdetails"
import ContributionResults from './components/contributionresults';
import JackpotDetails from './components/jackpotdetails';
import JackpotTickers from './components/jackpottickers';
import Auth from './components/auth';
import API from './api';

const VERSION = process.env.REACT_APP_VERSION;
const HOST = process.env.REACT_APP_HOST;
const PROTOCOL = process.env.REACT_APP_PROTOCOL;
const STREAM_PROTOCOL = process.env.REACT_APP_STREAM_PROTOCOL;
const API_URL = `${PROTOCOL}://${HOST}`;
const WS_URL = `${STREAM_PROTOCOL}://${HOST}/events`;

console.log(HOST);

let socket = null;

export default function App() {
  const api = API(API_URL);

  const [authToken, setAuthToken] = useState(null);
  const [authName, setAuthName] = useStorage("authName", "tt_jp_client");
  const [authBrand, setAuthBrand] = useStorage("authBrand", "thrilltech");
  const [authSecret, setAuthSecret] = useStorage("authSecret", "1234567890");

  const [walletId, setWalletId] = useStorage("walletId", "");
  const [brandId, setBrandId] = useStorage("brandId", "thrilltech:brand1");
  const [sourceId, setSourceId] = useStorage("source_id", "game1_slot");
  const [gameRoundId, setGameRoundId] = useStorage("gameRoundId", "1");
  const [playerId, setPlayerId] = useStorage("playerId", "player_00001");
  const [tokenId, setTokenId] = useStorage("tokenId", "token_00001");
  const [playerCountry, setPlayerCountry] = useStorage("playerCountry", "MT");
  const [baseWager, setBaseWager] = useStorage("baseWager", 2.5);
  const [currency, setCurrency] = useStorage("currency", "EUR");

  const [sourceList, setSourceList] = useState([]);
  const [resultItems, setResultItems] = useState([]);
  const [jackpotData, setJackpotData] = useState(null);
  const [jackpotTickerData, setJackpotTickerData] = useState({});

  useEffect(() => {
    if (authToken && !socket) {
      connectToEventStream()
    }
  }, [authToken])

  useEffect(() => {
    fetchSources();
  }, [authToken, brandId, playerId]);

  useEffect(() => {
    fetchJackpotData();
  }, [authToken, playerId, brandId, sourceId, playerCountry]);

  function useStorage(key, defVal) {
    let value = defVal;
    try {
      value = JSON.parse(localStorage.getItem(key)) ?? defVal;
    } catch (e) {}
    const [val, setVal] = useState(value);
    return [val, (val) => {
      setVal(val);
      localStorage.setItem(key, JSON.stringify(val));
    }];
  }

  function authenticate() {
    api.authenticate(authName, authBrand, authSecret).then(resp => {
      setAuthToken(resp.token);
    }).catch(e => {
      setAuthToken(null);
      console.error("authenticate", e);
    });
  }

  const connectToEventStream = () => {
    console.debug(`Connecting to ${WS_URL}`);
    socket = new WebSocket(WS_URL);

    socket.addEventListener('open', (e) => {
      console.debug("Connected to server")
      // send the auth message
      let msg = { 
        "action": { 
          "Authenticate": { 
            "auth_token": authToken 
          } 
        } 
      };
      socket.send(JSON.stringify(msg))
    })
    socket.addEventListener('message', (e) => {
      try {
        processMessage(JSON.parse(e.data));
      } catch (e) {
        console.error(e)
      }
    })
    socket.addEventListener('close', (e) => {
      console.debug("Lost connection to server");
      socket = null;
      setAuthToken(null);
      authToken();
    })
  }

  const processMessage = async (msg) => {
    switch (msg.event_type) {
      case "JackpotUpdateEvent":
        setJackpotTickerData(prev => ({
          ...prev,
          [msg.data.id]: msg.data,
        }));
        break;

      case "JackpotWinEvent":
        setResultItems(prev => [...prev, `${msg.data}`]);
        break;

      default:
        console.warn(`Unhandled message: ${msg.event_type}`);
    }
  }

  function fetchSources() {
    if (!authToken || !brandId) return;

    api.get_sources(brandId).then(sources => {
      console.debug("Sources", sources);
      setSourceList(sources);
    }).catch(e => {
      console.error("fetchSources", e);
      setSourceList([]);
    });
  }

  function fetchJackpotData() {
    if (!authToken || !brandId) return;

    api.get_jackpot_for_game(brandId, sourceId, playerId, playerCountry).then(jp => {
      console.debug("Jackpot", jp);
      setJackpotData(jp);
    }).catch(e => {
      console.error("fetchJackpotData", e);
      setJackpotData(null);
    });
  }

  function isOptInRequired() {
    return jackpotData && jackpotData.opt_in_required;
  }

  function isOptedIn() {
    return jackpotData && (jackpotData.opt_in_required === false ||
                           (jackpotData.optin_status && 
                            jackpotData.optin_status.opted_in));
  }

  function optInOut() {
    if (isOptInRequired()) {
      const optIn = !isOptedIn();
      api.opt_in_out({
        instance_id: jackpotData.id,
        brand_id: brandId,
        player_id: playerId,
        player_country: playerCountry,
        opt_in: optIn,
      }).then(() => {
        if (optIn) {
          setResultItems(prev => [...prev, `${playerId} opted in`]);
          console.debug("Opted In", playerId, jackpotData.id);
        } else {
          setResultItems(prev => [...prev, `${playerId} opted out`]);
          console.debug("Opted Out", playerId, jackpotData.id);
        }
      }).catch(e => {
        console.error("optInOut", e);
        setResultItems(prev => [...prev, e]);
      }).finally(() => {
        fetchJackpotData();
      });
    }
  }

  function contribute() {
    if (isOptedIn()) {
      api.contribute({
        token: tokenId,
        wallet_id: walletId,
        brand_id: brandId,
        source_id: sourceId,
        gameround_id: gameRoundId,
        player_id: playerId,
        player_country: playerCountry,
        base_wager: baseWager,
        currency: currency,
      }).then(result => {
        console.debug("Contributed", playerId, result);
        setResultItems(prev => [...prev, result]);
      }).catch(e => {
        console.error("contribute", e);
        setResultItems(prev => [...prev, e]);
      }).finally(() => {
        fetchJackpotData();
      });
    }
  }

  function onAuthChanged(key, value) {
    switch (key) {
      case "name":
        setAuthName(value);
        break;
      case "brand":
        setAuthBrand(value);
        break;
      case "secret":
        setAuthSecret(value);
        break;
      default:
        console.error("onAuthChanged: Incorrect key:", key);
        break;
    }
  }

  function onInputDetailsChanged(key, value) {
    switch (key) {
      case "walletId":
        setWalletId(value);
        break;
      case "brandId":
        setBrandId(value);
        break;
      case "sourceId":
        setSourceId(value);
        break;
      case "gameRoundId":
        setGameRoundId(value);
        break;
      case "tokenId":
        setTokenId(value);
        break;
      case "playerId":
        setPlayerId(value);
        break;
      case "playerCountry":
        setPlayerCountry(value);
        break;
      case "baseWager":
        setBaseWager(parseFloat(value));
        break;
      case "currency":
        setCurrency(value);
        break;
      default:
        console.error("onInputDetailsChanged: Incorrect key:", key);
        break;
    }
  }

  return (
    <div className="App">
      <Header title="Thrillpots Certification Client" version={VERSION}></Header>
      <Auth
        name={authName}
        brand={authBrand}
        secret={authSecret}
        onDataChanged={onAuthChanged}
        onClick={authenticate}
      />
      <br />
      <InputDetails
        walletId={walletId}
        brandId={brandId}
        sourceId={sourceId}
        gameRoundId={gameRoundId}
        tokenId={tokenId}
        playerId={playerId}
        playerCountry={playerCountry}
        baseWager={baseWager}
        currency={currency}
        sourceList={sourceList}
        onDataChanged={onInputDetailsChanged}
      />
      <button
        className="OptinButton"
        disabled={!isOptInRequired(jackpotData)}
        onClick={optInOut}>
          {!isOptInRequired() ? "Opt In/Out" :
           !isOptedIn() ? "Opt In" : "Opt Out"}
      </button>
      <button
        className="SpinButton"
        disabled={!isOptedIn()}
        onClick={contribute}>
          Contribute
      </button>
      <JackpotTickers
        tickerData={jackpotTickerData}
      />
      <div className="InfoArea">
        <ContributionResults
          items={resultItems}
        />
        <JackpotDetails
          jackpotData={jackpotData}
        />
      </div>
    </div>
  );
}
